import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { NAV_ITEMS } from "@/lib/nav";
import { useLang } from "@/i18n/LanguageContext";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/auth/AuthContext";
import { resolveDemoNavPath } from "@/lib/demoNavigation";

const GROUPS = [
  { key: "nav_group_main", ids: ["dashboard", "data-hub", "markets", "opportunities"] },
  { key: "nav_group_growth", ids: ["partners", "campaigns", "performance"] },
  { key: "nav_group_system", ids: ["ask-lodge", "gsa-worldwide", "settings"] },
];

const useGroups = () => {
  const location = useLocation();
  const { activeMembership, activeOrgId } = useAuth();
  const org = activeMembership?.org || {};
  const immutableDemo = org.is_demo && org.demo_mode === "immutable";

  return useMemo(
    () =>
      GROUPS.map((group) => ({
        ...group,
        items: group.ids.map((id) => NAV_ITEMS.find((n) => n.id === id)).filter(Boolean).map((item) => ({
          ...item,
          to: resolveDemoNavPath({ path: item.path, location, activeOrgId, immutableDemo }),
        })),
      })),
    [activeOrgId, immutableDemo, location]
  );
};

/* ---------- Rail (level 1 · very dark graphite, icons only) ---------- */
const Rail = ({ onNavigate }) => {
  const location = useLocation();
  const groups = useGroups();

  return (
    <div
      data-testid="app-rail"
      className="rail-scroll flex h-full w-[72px] shrink-0 flex-col items-center overflow-y-auto bg-lodge-graphite py-4"
    >
      <NavLink to="/dashboard" onClick={onNavigate} aria-label="Lodge & Co" className="mb-2">
        <Logo onDark className="h-8" />
      </NavLink>

      <nav className="mt-4 flex flex-1 flex-col items-center gap-6">
        {groups.map((group, gi) => (
          <div key={group.key} className="flex flex-col items-center gap-1.5">
            {gi > 0 && <span className="mb-2 h-px w-6 bg-white/10" aria-hidden />}
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  onClick={onNavigate}
                  title={item.label}
                  data-testid={`rail-link-${item.id}`}
                  className={`group relative flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200 ${
                    active
                      ? "bg-lodge-deep text-lodge-mint"
                      : "text-white/45 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  {active && (
                    <span className="absolute -left-[14px] top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-lodge-mint" />
                  )}
                  <Icon size={19} strokeWidth={2} />
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
    </div>
  );
};

/* ---------- Secondary panel (level 2 · deep green, labelled) ---------- */
const SecondaryPanel = ({ onNavigate, onToggle }) => {
  const { t } = useLang();
  const location = useLocation();
  const groups = useGroups();

  return (
    <div
      data-testid="app-nav-panel"
      className="panel-scroll flex h-full w-[212px] shrink-0 flex-col overflow-y-auto bg-lodge-deep"
    >
      <div className="flex h-16 items-center justify-between px-4">
        <div className="leading-tight">
          <div className="font-heading text-[15px] font-bold tracking-tight text-white">
            Lodge <span className="text-lodge-mint">&amp; Co</span>
          </div>
          <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-white/40">
            Tourism Intelligence OS
          </div>
        </div>
        {onToggle && (
          <button
            type="button"
            data-testid="sidebar-collapse-btn"
            onClick={onToggle}
            title="Réduire le panneau"
            className="hidden h-7 w-7 items-center justify-center rounded-lg text-white/45 transition-colors hover:bg-white/10 hover:text-white lg:flex"
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-6 px-3 py-3">
        {groups.map((group) => (
          <div key={group.key}>
            <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
              {t(group.key)}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    onClick={onNavigate}
                    data-testid={`sidebar-link-${item.id}`}
                    className={`group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-all duration-200 ${
                      active
                        ? "bg-white/[0.09] font-semibold text-white"
                        : "font-medium text-white/55 hover:bg-white/[0.05] hover:text-white"
                    }`}
                  >
                    {active && (
                      <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-lodge-mint" />
                    )}
                    <Icon size={17} strokeWidth={2} className={active ? "text-lodge-mint" : "text-current"} />
                    <span className="tracking-tight">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
          <p className="text-[11px] font-semibold text-white">{t("sidebar_footer")}</p>
          <p className="mt-0.5 text-[10px] text-white/40">v0.1 · Preview</p>
        </div>
      </div>
    </div>
  );
};

/* Combined inner (used by the mobile drawer — always fully expanded). */
export const SidebarInner = ({ onNavigate }) => (
  <div className="flex h-full">
    <Rail onNavigate={onNavigate} />
    <SecondaryPanel onNavigate={onNavigate} />
  </div>
);

/* Desktop fixed sidebar with a retractable secondary panel (open by default). */
export const Sidebar = ({ collapsed = false, onToggle, onExpand }) => (
  <aside
    data-testid="app-sidebar"
    className="fixed left-0 top-0 z-30 hidden h-screen shadow-lodge-rail lg:flex"
  >
    <Rail />
    {collapsed ? (
      <button
        type="button"
        data-testid="sidebar-expand-btn"
        onClick={onExpand}
        title="Afficher le panneau"
        className="flex h-full w-8 items-center justify-center bg-lodge-deep text-white/45 transition-colors hover:bg-lodge-deep-soft hover:text-white"
      >
        <PanelLeftOpen size={16} />
      </button>
    ) : (
      <SecondaryPanel onToggle={onToggle} />
    )}
  </aside>
);
